// Built-in home stock location/category suggestions — labels follow employer content language.

import { LANGUAGE, SUPPORTED_LANGUAGES, type Language } from '@fdh/shared';
import { UI_MESSAGES } from '../i18n/uiMessages';

export type InventoryMetadataSuggestionItem = {
  label: string;
  value: string;
};

const LOCATION_SUGGESTION_DEFS = [
  { key: 'kitchen', emoji: '🍳' },
  { key: 'fridge', emoji: '🧊' },
  { key: 'storeRoom', emoji: '📦' },
  { key: 'livingRoom', emoji: '🛋️' },
  { key: 'bathroomA', emoji: '🚿' },
  { key: 'bathroomB', emoji: '🚽' },
] as const;

const CATEGORY_SUGGESTION_DEFS = [
  { key: 'food', emoji: '🍎' },
  { key: 'drinks', emoji: '🥤' },
  { key: 'babyFood', emoji: '🍼' },
  { key: 'babyDrinks', emoji: '🧃' },
  { key: 'elderlyFood', emoji: '👴' },
  { key: 'petFood', emoji: '🐶' },
  { key: 'dryGoods', emoji: '📦' },
  { key: 'chilledGoods', emoji: '❄️' },
] as const;

const locationMessageKey = (key: string): string => `task.inventory.suggest.location.${key}`;
const categoryMessageKey = (key: string): string => `task.inventory.suggest.category.${key}`;

export const normalizeEmployerContentLanguage = (
  settingsLanguage: string | undefined | null,
  fallback: Language,
): Language => {
  if (settingsLanguage == null) {
    return fallback;
  }
  if (settingsLanguage === 'zh-HK' || settingsLanguage === 'zh-Hant') {
    return LANGUAGE.ZH_HANT;
  }
  if (settingsLanguage === 'zh-CN' || settingsLanguage === 'zh-Hans') {
    return LANGUAGE.ZH_HANS;
  }
  if (settingsLanguage === 'en') {
    return LANGUAGE.EN;
  }
  if (settingsLanguage === 'id' || settingsLanguage === 'in') {
    return LANGUAGE.ID;
  }
  if (settingsLanguage === 'fil' || settingsLanguage === 'tl') {
    return LANGUAGE.FIL;
  }
  return (SUPPORTED_LANGUAGES as readonly string[]).includes(settingsLanguage)
    ? (settingsLanguage as Language)
    : fallback;
};

const resolveSuggestionText = (language: Language, messageKey: string): string => {
  const primary = UI_MESSAGES[language]?.[messageKey]?.trim();
  if (primary) {
    return primary;
  }
  const english = UI_MESSAGES[LANGUAGE.EN][messageKey]?.trim();
  if (english) {
    return english;
  }
  const traditional = UI_MESSAGES[LANGUAGE.ZH_HANT][messageKey]?.trim();
  return traditional ?? messageKey;
};

const buildSuggestions = (
  language: Language,
  defs: ReadonlyArray<{ key: string; emoji: string }>,
  messageKeyFor: (key: string) => string,
): InventoryMetadataSuggestionItem[] =>
  defs.map(({ key, emoji }) => {
    const value = resolveSuggestionText(language, messageKeyFor(key));
    return { value, label: `${emoji} ${value}` };
  });

export const buildInventoryLocationSuggestions = (
  language: Language,
): InventoryMetadataSuggestionItem[] =>
  buildSuggestions(language, LOCATION_SUGGESTION_DEFS, locationMessageKey);

export const buildInventoryCategorySuggestions = (
  language: Language,
): InventoryMetadataSuggestionItem[] =>
  buildSuggestions(language, CATEGORY_SUGGESTION_DEFS, categoryMessageKey);

export const mergeInventoryMetadataSuggestionChips = (
  base: readonly InventoryMetadataSuggestionItem[],
  history: readonly string[],
): string[] => {
  const seen = new Set<string>();
  const merged: string[] = [];
  const append = (value: string) => {
    const trimmed = value.trim();
    if (trimmed === '' || seen.has(trimmed)) {
      return;
    }
    seen.add(trimmed);
    merged.push(trimmed);
  };
  for (const row of base) {
    append(row.value);
  }
  for (const value of history) {
    append(value);
  }
  return merged;
};

export const buildInventoryMetadataSuggestionLabelMap = (
  suggestions: readonly InventoryMetadataSuggestionItem[],
): Map<string, string> => new Map(suggestions.map((row) => [row.value, row.label]));
